import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Star, Trash2, Wallet, ShieldAlert, CheckCircle2, MailWarning, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full bg-ink border-3 border-black px-3 py-2.5 font-mono text-sm text-cream focus:outline-none focus:border-neon";

function ChainTag({ type }) {
  const sol = type === "solana";
  return <span className={`tag !text-[9px] !px-2 !py-0.5 ${sol ? "bg-grape text-cream" : "bg-neon text-black"}`}>{sol ? "SOLANA" : "EVM"}</span>;
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({
    username: user?.username || "", displayName: user?.displayName || "", bio: user?.bio || "",
  });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#wallets") {
      document.getElementById("wallets")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  if (!user) return null;

  const saveProfile = async () => {
    setBusy("profile");
    try { await api.put("/profile", profile); await refresh(); toast.success("Profile saved."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const changePw = async () => {
    setBusy("pw");
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password || null, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "" });
      toast.success(user.hasPassword ? "Password changed." : "Password set.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const setPrimary = async (id) => { try { await api.post(`/wallets/${id}/primary`); await refresh(); toast.success("Primary wallet updated."); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const unlink = async (id) => { try { await api.delete(`/wallets/${id}`); await refresh(); toast.success("Wallet disconnected."); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const resendVerify = async () => { try { await api.post("/auth/resend-verification"); toast.success("Verification email sent (check inbox)."); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
      <span className="tag">Account</span>
      <h1 className="headline text-cream text-4xl mt-5">ACCOUNT <span className="text-neon">SETTINGS</span></h1>

      {/* Identity summary */}
      <div className="comic-card p-5 mt-8">
        <h3 className="font-display font-900 text-cream uppercase text-sm">Login Methods</h3>
        <div className="flex flex-wrap gap-2 mt-3">
          {(user.authProviders || []).map((p) => (
            <span key={p} className="tag !text-[10px]">{p === "google" ? "Google" : p === "wallet" ? "Wallet" : "Email"}</span>
          ))}
        </div>
        {user.email && (
          <div className="flex items-center gap-2 mt-3 font-mono text-xs">
            {user.emailVerified
              ? <><CheckCircle2 className="w-4 h-4 text-neon" /> <span className="text-cream">{user.email} · verified</span></>
              : <><MailWarning className="w-4 h-4 text-gold" /> <span className="text-dim">{user.email} · not verified</span>
                  <button onClick={resendVerify} className="text-neon underline ml-1" data-testid="resend-verify">Resend</button></>}
          </div>
        )}
      </div>

      {/* Profile */}
      <div className="comic-card p-6 mt-6 space-y-4">
        <h3 className="font-display font-900 text-neon uppercase text-sm">Profile</h3>
        <div>
          <label className="font-mono text-[10px] uppercase text-dim">Username</label>
          <input className={`${inputCls} mt-1`} value={profile.username} onChange={(e) => setProfile({ ...profile, username: e.target.value })} placeholder="anon_handle" data-testid="profile-username" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-dim">Display Name</label>
          <input className={`${inputCls} mt-1`} value={profile.displayName} onChange={(e) => setProfile({ ...profile, displayName: e.target.value })} data-testid="profile-displayname" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-dim">Bio</label>
          <textarea rows={2} className={`${inputCls} mt-1 resize-none`} value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} placeholder="Chronically robbed since 2021" data-testid="profile-bio" />
        </div>
        <button onClick={saveProfile} disabled={busy === "profile"} className="btn-neon disabled:opacity-50" data-testid="save-profile">Save Profile</button>
      </div>

      {/* Connected wallets */}
      <div id="wallets" className="comic-card p-6 mt-6 scroll-mt-24">
        <h3 className="font-display font-900 text-neon uppercase text-sm flex items-center gap-2"><Wallet className="w-4 h-4" /> Connected Wallets</h3>
        <div className="space-y-3 mt-4">
          {user.wallets?.length ? user.wallets.map((w) => (
            <div key={w.walletId} className="flex items-center justify-between border-3 border-black bg-ink p-3" data-testid={`wallet-${w.walletId}`}>
              <div className="flex items-center gap-3 min-w-0">
                <ChainTag type={w.chainType} />
                <span className="font-mono text-xs text-cream truncate">{w.shortAddress}</span>
                {w.isPrimary && <Star className="w-4 h-4 text-gold fill-gold" />}
                <button onClick={() => { navigator.clipboard.writeText(w.address); toast.success("Copied"); }} className="text-neon"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex gap-2">
                {!w.isPrimary && <button onClick={() => setPrimary(w.walletId)} className="font-mono text-[10px] uppercase text-dim hover:text-neon" data-testid={`primary-${w.walletId}`}>Set Primary</button>}
                <button onClick={() => unlink(w.walletId)} className="text-danger" data-testid={`unlink-${w.walletId}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          )) : <p className="font-mono text-xs text-dim">No wallets linked yet. Wallet connection opens when $ROBBED launches.</p>}
        </div>
        <div className="mt-4">
          <button type="button" disabled title="Wallet connection will be available when $ROBBED launches."
            className="btn-ghost !border-charcoal !text-dim opacity-60 cursor-not-allowed !py-2 !text-xs" data-testid="connect-wallet-disabled">
            <Lock className="w-4 h-4" /> Connect Wallet
          </button>
          <p className="font-mono text-[10px] text-dim mt-2">Available when $ROBBED launches — EVM &amp; Solana wallet connection will unlock at token launch.</p>
        </div>
        <div className="flex items-start gap-2 mt-4 font-mono text-[11px] text-dim">
          <ShieldAlert className="w-4 h-4 text-neon shrink-0" /> Balances are never combined across chains and won't appear until $ROBBED is live. Signing only proves ownership — it never moves funds.
        </div>
      </div>

      {/* Password */}
      <div className="comic-card p-6 mt-6 space-y-4">
        <h3 className="font-display font-900 text-neon uppercase text-sm">{user.hasPassword ? "Change Password" : "Set A Password"}</h3>
        {user.hasPassword && (
          <input type="password" className={inputCls} placeholder="Current password" value={pw.current_password}
            onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password" />
        )}
        <input type="password" className={inputCls} placeholder="New password (min 6 chars)" value={pw.new_password}
          onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password" />
        <button onClick={changePw} disabled={busy === "pw" || pw.new_password.length < 6} className="btn-neon disabled:opacity-50" data-testid="save-password">
          {user.hasPassword ? "Change Password" : "Set Password"}
        </button>
      </div>
    </div>
  );
}
